import { Link } from "react-router-dom";
import { Award, Clock, BadgeEuro, ArrowRight, Calendar } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import SEOHead from "@/components/SEOHead";
import StaticStats from "@/components/StaticStats";
import HeroPremium from "@/components/HeroPremium";
import { CTA_DEVIS } from "@/data/forms";

const values = [
  { icon: Award, title: "Travail professionnel", desc: "Une équipe de professionnels qualifiés, encadrée par Daniel Gheorghe, 10 ans d'expérience dans le bâtiment. Des finitions soignées sur chaque chantier." },
  { icon: Clock, title: "Respect des délais", desc: "Nous respectons toujours nos engagements. Chaque projet est planifié et exécuté dans les délais convenus, sans mauvaise surprise." },
  { icon: BadgeEuro, title: "Tarifs satisfaisants", desc: "Les meilleurs prix pour nos travaux. Nous offrons un excellent rapport qualité-prix pour tous nos services de construction et rénovation." },
];

export default function About() {
  return (
    <div data-testid="about-page" style={{ backgroundColor: '#ffffff', color: '#1a1a1a' }}>
      <SEOHead title="À propos | VGI Construct – Entreprise générale de rénovation créée en 2018" description="Découvrez VGI Construct SRL : société créée en 2018 par Daniel Gheorghe, 10 ans d'expérience dans le bâtiment. Active à Bruxelles et dans tout le Brabant. 67 chantiers livrés, 5,0/5 sur Google." path="/a-propos" />
      <HeroPremium
        badge="Notre histoire"
        titleWhite="Depuis 2018, la passion"
        titleRed="de la rénovation"
        photo="https://images.unsplash.com/photo-1770822662967-7f66605f9103?w=700&q=85&fm=webp"
        photoAlt="Ouvriers bétonneuse VGI Construct"
        photoBadgeIcon={Calendar}
        photoBadgeTitle="Créée en 2018"
        photoBadgeSub="67 chantiers livrés"
        decoNumber="02"
      />

      <section style={{ backgroundColor: '#ffffff', padding: '64px 0 80px' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '48px', alignItems: 'center' }} className="lg:!grid-cols-2">
            <div>
              <ScrollReveal>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ width: '4px', height: '40px', backgroundColor: '#8B0000', borderRadius: '2px' }} />
                  <p style={{ textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.2em', fontWeight: 700, color: '#8B0000', margin: 0 }}>Notre histoire</p>
                </div>
                <p data-testid="about-founder" style={{ color: '#444444', fontSize: '17px', lineHeight: 1.8, margin: '0 0 20px' }}><strong>Société créée en 2018 par Daniel Gheorghe, 10 ans d'expérience dans le bâtiment.</strong> VGI Construct SRL est une entreprise générale de rénovation basée à Sint-Pieters-Leeuw, active à Bruxelles et dans tout le Brabant.</p>
                <p style={{ color: '#444444', fontSize: '17px', lineHeight: 1.8, margin: '0 0 20px' }}>Nous sommes spécialisés dans la rénovation complète, les extensions, le gros-œuvre, la toiture, les châssis, l'isolation, le plafonnage, les chapes et carrelages, ainsi que l'électricité, la plomberie et le chauffage.</p>
                <p style={{ color: '#444444', fontSize: '17px', lineHeight: 1.8, margin: 0 }}>Chez VGI Construct, nous sommes passionnés par notre métier et nous nous engageons à fournir des résultats exceptionnels : 67 chantiers livrés et une note de 5,0/5 sur Google (plus de 20 avis).</p>
              </ScrollReveal>
            </div>
            <div>
              <ScrollReveal delay={200}>
                <div style={{ position: 'relative' }}>
                  <img src="https://images.unsplash.com/photo-1761227447538-f4e1ec0a9df6?w=600&q=80&fm=webp" alt="Équipe VGI Construct" loading="lazy" style={{ width: '100%', aspectRatio: '4/5', objectFit: 'cover', borderRadius: '6px' }} />
                  <div style={{ position: 'absolute', bottom: '-20px', left: '-20px', backgroundColor: '#8B0000', padding: '20px', borderRadius: '6px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }} className="max-sm:!left-0">
                    <span style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '2.5rem', fontWeight: 900, color: '#ffffff' }}>2018</span>
                    <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', fontWeight: 700, marginTop: '4px' }}>Année de création</p>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="values-section" style={{ backgroundColor: '#f5f5f5', padding: '80px 0' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px' }}>
          <ScrollReveal>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
              <div style={{ width: '4px', height: '40px', backgroundColor: '#8B0000', borderRadius: '2px' }} />
              <p style={{ textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.2em', fontWeight: 700, color: '#8B0000', margin: 0 }}>Nos valeurs</p>
            </div>
            <h2 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 900, color: '#1a1a1a', marginBottom: '48px' }}>Ce qui nous définit</h2>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '24px' }}>
            {values.map((val, i) => (
              <ScrollReveal key={val.title} delay={i * 150}>
                <div style={{ backgroundColor: '#ffffff', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '32px' }}>
                  <val.icon size={36} color="#8B0000" style={{ marginBottom: '20px' }} strokeWidth={1.5} />
                  <h3 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '20px', fontWeight: 900, color: '#1a1a1a', marginBottom: '12px' }}>{val.title}</h3>
                  <p style={{ color: '#444444', fontSize: '14px', lineHeight: 1.8, margin: 0 }}>{val.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section style={{ backgroundColor: '#2d2d2d', padding: '48px 0' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px' }}>
          <StaticStats />
        </div>
      </section>

      <section style={{ backgroundColor: '#ffffff', padding: '80px 24px', textAlign: 'center' }}>
        <ScrollReveal>
          <h2 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', fontWeight: 900, color: '#1a1a1a', marginBottom: '24px' }}>Un projet en tête ?</h2>
          <p style={{ color: '#444444', fontSize: '18px', marginBottom: '32px', maxWidth: '500px', marginLeft: 'auto', marginRight: 'auto' }}>Contactez-nous pour un devis gratuit et sans engagement.</p>
          <Link to="/demande-devis" data-testid="about-cta" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: '#8B0000', color: '#ffffff', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '14px 36px', textDecoration: 'none', fontSize: '14px', borderRadius: '100px' }}>
            {CTA_DEVIS} <ArrowRight size={16} />
          </Link>
        </ScrollReveal>
      </section>
    </div>
  );
}
